"use client";

import { useEffect, useSyncExternalStore } from "react";
import { fetchOpeners, type EventOpeners } from "@/lib/multibook/fetch";
import { getOddsLiveClient } from "@/lib/odds-screen/live/client";
import { displayOdds } from "@/lib/odds-screen/live/derive";
import type { BookId, EventId, FamilyId, LeagueId, MarketSpec, OddsDisplay } from "@/lib/odds-screen/live/types";
import { cellLineLabel } from "./labels";

export const OPENER_BOOK: BookId = "pinnacle";
const OPENERS_FAMILY: FamilyId = "game_lines";
const BATCH = 50;
const RETRY_MS = 60_000;
const MAX_IN_FLIGHT = 2;

export type OpenPrice = { readonly display: string; readonly line: string | null };
export type OpenCell = OpenPrice | "none" | null;

export function hasOpeners(family: FamilyId): boolean {
  return family === OPENERS_FAMILY;
}

export function openPrices(
  openers: EventOpeners | undefined,
  spec: MarketSpec,
  main: number | null,
  outcomes: readonly number[],
  format: OddsDisplay,
): readonly OpenCell[] {
  const sides = openers?.[spec.id]?.[OPENER_BOOK];
  return outcomes.map((outcome) => {
    if (openers === undefined) return null;
    const name = spec.outcomes[outcome];
    const quote = sides?.[name];
    if (quote === undefined) return "none";
    const openMain = spec.line === "spread" && name === "away" ? -quote.line : quote.line;
    const moved = main === null || Math.abs(openMain - main) >= 0.001;
    return {
      display: displayOdds(quote.price_decimal, format),
      line: spec.line === "none" || !moved ? null : cellLineLabel(spec, openMain, outcome),
    };
  });
}

const listeners = new Set<() => void>();
const inFlight = new Set<string>();
const failedAt = new Map<string, number>();
const done = new Set<EventId>();
const EMPTY: ReadonlyMap<EventId, EventOpeners> = new Map();
let loaded = EMPTY;

export function resetOpenersForTests(): void {
  inFlight.clear();
  failedAt.clear();
  done.clear();
  loaded = EMPTY;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function merge(batch: readonly EventId[], events: Record<EventId, EventOpeners>) {
  const next = new Map(loaded);
  for (const id of batch) {
    const event: EventOpeners = { ...next.get(id) };
    for (const [market, books] of Object.entries(events[id] ?? {})) {
      const sides = books[OPENER_BOOK];
      if (sides !== undefined) event[market] = { [OPENER_BOOK]: { ...event[market]?.[OPENER_BOOK], ...sides } };
    }
    next.set(id, event);
    done.add(id);
  }
  loaded = next;
}

function load(batch: readonly EventId[], key: string) {
  inFlight.add(key);
  fetchOpeners(batch)
    .then((events) => merge(batch, events), () => failedAt.set(key, Date.now()))
    .finally(() => {
      inFlight.delete(key);
      for (const listener of listeners) listener();
    });
}

function kickoffOrder(events: Iterable<{ readonly id: EventId; readonly startMs: number }>): EventId[] {
  return [...events].sort((a, b) => a.startMs - b.startMs || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map((event) => event.id);
}

function chunks(ids: readonly EventId[]): EventId[][] {
  return Array.from({ length: Math.ceil(ids.length / BATCH) }, (_, index) => ids.slice(index * BATCH, (index + 1) * BATCH));
}

function batches(league: LeagueId, wanted: ReadonlySet<EventId>): EventId[][] {
  const client = getOddsLiveClient();
  const board = kickoffOrder(client.read({ kind: "board", league, family: OPENERS_FAMILY }).value?.events.values() ?? []);
  const live = kickoffOrder(client.read({ kind: "live", league }).value?.games.values() ?? []).filter((id) => !board.includes(id));
  const unlisted = [...wanted].filter((id) => !board.includes(id) && !live.includes(id)).sort();
  return [...chunks(board), ...chunks(live), ...chunks(unlisted)];
}

export function useOpeners(league: LeagueId, eventIds: readonly EventId[]): ReadonlyMap<EventId, EventOpeners> {
  const key = eventIds.join(",");
  useEffect(() => {
    if (key === "") return;
    const wanted = new Set(key.split(","));
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = () => {
      clearTimeout(timer);
      const now = Date.now();
      let wait = Infinity;
      for (const batch of batches(league, wanted)) {
        const batchKey = batch.join(",");
        if (inFlight.has(batchKey) || batch.every((id) => !wanted.has(id) || done.has(id))) continue;
        const retryAt = (failedAt.get(batchKey) ?? -Infinity) + RETRY_MS;
        if (now < retryAt) wait = Math.min(wait, retryAt - now);
        else if (inFlight.size < MAX_IN_FLIGHT) load(batch, batchKey);
      }
      if (wait < Infinity) timer = setTimeout(run, wait);
    };
    const unsubscribe = subscribe(run);
    run();
    return () => { unsubscribe(); clearTimeout(timer); };
  }, [league, key]);
  return useSyncExternalStore(subscribe, () => loaded, () => EMPTY);
}
